import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Boxes, Plus, Minus, Check, Search } from "lucide-react";
import { api, fileUrl, fmtMoney } from "@/lib/api";
import { usePageMeta, useBuildPalletServiceSchema } from "@/lib/seo";
import { useCart } from "@/context/CartContext";
import { PalletFinalSale } from "@/components/PalletFinalSale";

export default function PalletBuilderPage() {
  usePageMeta(
    "Build Your Own Pallet — Custom Liquidation Pallets Grand Rapids",
    "Customize your own liquidation pallet at IPVALUE MARKETPLACE, Grand Rapids MI: 3 items $500, 5 items $900, 7 items $1,500, 11 items $2,200 — merchandise included, plus a flat $299 build fee."
  );
  useBuildPalletServiceSchema();
  const navigate = useNavigate();
  const { addPallet } = useCart();
  const [cfg, setCfg] = useState(null);
  const [tier, setTier] = useState(null);
  const [products, setProducts] = useState([]);
  const [query, setQuery] = useState("");
  const [cat, setCat] = useState("all");
  const [picks, setPicks] = useState({}); // product_id -> count
  const [ageOk, setAgeOk] = useState(false);
  const [agePrompt, setAgePrompt] = useState(null); // product pending 21+ confirmation

  useEffect(() => {
    api.get("/pallet-builder").then((r) => {
      setCfg(r.data);
      if (r.data.tiers?.length) setTier(r.data.tiers[0]);
    });
    api.get("/products", { params: { limit: 100, custom_pallet_eligible: true } }).then((r) =>
      setProducts(r.data.items.filter((p) => !p.is_pallet && p.stock > 0))
    );
  }, []);

  const fee = Number(cfg?.customization_fee ?? 299);

  const selectedCount = useMemo(
    () => Object.values(picks).reduce((s, c) => s + c, 0),
    [picks]
  );
  const target = tier ? tier.item_count : 0;
  const remaining = target - selectedCount;

  const categories = useMemo(
    () => [...new Set(products.map((p) => p.category))].sort(),
    [products]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = products;
    if (cat !== "all") list = list.filter((p) => p.category === cat);
    if (!q) return list;
    return list.filter((p) => p.name.toLowerCase().includes(q) || p.category.toLowerCase().includes(q));
  }, [products, query, cat]);

  const isAgeRestricted = (p) =>
    p.category === "Tobacco Accessories" || /\blighter/i.test(p.name || "");

  const adjust = (p, delta) => {
    if (delta > 0 && !ageOk && isAgeRestricted(p)) {
      setAgePrompt(p);
      return;
    }
    setPicks((prev) => {
      const cur = prev[p.id] || 0;
      const next = cur + delta;
      if (delta > 0) {
        if (selectedCount >= target) {
          toast.error(`Your ${target}-item pallet is full — remove something first or pick a bigger pallet`);
          return prev;
        }
        if (next > p.stock) {
          toast.error(`Only ${p.stock} of ${p.name} in stock`);
          return prev;
        }
      }
      const copy = { ...prev };
      if (next <= 0) delete copy[p.id];
      else copy[p.id] = next;
      return copy;
    });
  };

  const addToCart = () => {
    if (remaining > 0) {
      toast.error(`Pick ${remaining} more item${remaining > 1 ? "s" : ""} to complete your pallet`);
      return;
    }
    const ids = [];
    const names = [];
    Object.entries(picks).forEach(([pid, cnt]) => {
      const p = products.find((x) => x.id === pid);
      for (let i = 0; i < cnt; i++) ids.push(pid);
      if (p) names.push(`${cnt}× ${p.name}`);
    });
    addPallet(tier, fee, ids, names);
    toast.success(`${target}-item custom pallet added — ${fmtMoney(tier.price)} + ${fmtMoney(fee)} customization fee`);
    navigate("/cart");
  };

  if (!cfg) return <div className="p-20 text-center ipv-eyebrow">Loading…</div>;
  if (!cfg.enabled)
    return <div className="p-20 text-center ipv-eyebrow">Pallet builder is currently unavailable — call us at +1 (616) 410-0158.</div>;

  return (
    <div data-testid="pallet-builder-page">
      {/* HERO */}
      <section className="bg-[#0A1C40] border-b-4 border-[#FFC220]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 py-14 lg:py-16">
          <div className="inline-flex items-center gap-2 bg-[#FFC220] text-black px-3 py-1 text-xs font-black tracking-[0.25em] uppercase">
            <Boxes className="w-3.5 h-3.5" /> Wholesale
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter mt-5 text-white leading-[0.95]">
            {cfg.headline}
          </h1>
          <p className="mt-5 text-white/70 max-w-lg leading-relaxed">{cfg.description}</p>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 py-10 lg:py-14">
        {/* STEP 1 — tier */}
        <div className="ipv-eyebrow mb-4">Step 1 — Choose your pallet size</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 max-w-4xl">
          {cfg.tiers.map((t) => {
            const active = tier?.item_count === t.item_count;
            return (
              <button
                key={t.item_count}
                type="button"
                onClick={() => { setTier(t); setPicks({}); }}
                className={`relative border-2 p-5 text-left [touch-action:manipulation] ${
                  active ? "border-[#FFC220] bg-amber-50" : "border-gray-200"
                }`}
                data-testid={`pallet-tier-${t.item_count}`}
              >
                {active && (
                  <span className="absolute top-3 right-3 w-6 h-6 rounded-full bg-[#FFC220] flex items-center justify-center">
                    <Check className="w-4 h-4 text-black" strokeWidth={3} />
                  </span>
                )}
                <div className="font-display font-black text-xl leading-tight">{t.item_count}-ITEM CUSTOM PALLET</div>
                <div className="font-display font-black text-2xl mt-2 text-[#C99700]">{fmtMoney(t.price)}</div>
                <div className="text-[10px] font-black tracking-wide text-green-700 mt-0.5">MERCHANDISE INCLUDED</div>
                <div className="text-[10px] font-black tracking-wide text-[#0A1C40]">+ {fmtMoney(fee)} CUSTOMIZATION FEE</div>
              </button>
            );
          })}
        </div>
        <div className="mt-4 max-w-4xl text-xs text-gray-700 bg-gray-50 border border-gray-200 p-3 leading-relaxed" data-testid="custom-fee-explainer">
          Your pallet price <b>INCLUDES the merchandise</b> you pick below. A flat <b>{fmtMoney(fee)} customization
          fee</b> covers selecting, handling, building, wrapping, and preparing your pallet — it's charged automatically
          together with the pallet price at checkout. No hidden charges, no separate merchandise billing.{" "}
          <b>{cfg.lead_time_text || "Ready for pickup or delivery in 2–3 business days"}.</b> Custom pallets are not
          eligible for the 10% member discount.
        </div>
        <div className="mt-3 max-w-4xl grid sm:grid-cols-4 gap-2 text-[11px]" data-testid="custom-process-steps">
          {["1 · Pick your items below", `2 · Pay pallet price + ${fmtMoney(fee)} fee at checkout`, "3 · We build & wrap — ready in 2–3 business days", "4 · Pickup or local delivery"].map((s, i) => (
            <div key={i} className="border border-[#0A1C40]/20 bg-white px-2.5 py-2 font-semibold text-[#0A1C40]">{s}</div>
          ))}
        </div>
        <div className="mt-3 max-w-4xl text-[11px] text-gray-600" data-testid="custom-inventory-note">
          Selections come <b>only from our designated Custom Pallet Selection inventory</b> shown below — real, live
          in-stock availability with photos. Not showroom items, individually listed retail items, or existing
          pre-built pallets. Prefer to build it together? Call/text (616) 410-0158 and we'll build it with you.
        </div>
        <div className="mt-4 max-w-4xl"><PalletFinalSale compact /></div>

        {/* STEP 2 — pick items */}
        <div className="flex flex-wrap items-center justify-between gap-4 mt-12 mb-3">
          <div className="ipv-eyebrow">Step 2 — Pick your {target} items</div>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              className="input-base !pl-9 !w-64"
              placeholder="Search items…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="pallet-search"
            />
          </div>
        </div>
        {categories.length > 1 && (
          <div className="flex gap-2 flex-wrap mb-4" data-testid="pallet-category-chips">
            {["all", ...categories].map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setCat(c)}
                className={`px-3.5 py-1.5 rounded-full border-2 text-xs font-bold [touch-action:manipulation] ${
                  cat === c ? "border-[#0A1C40] bg-[#0A1C40] text-white" : "border-gray-300 text-gray-700 hover:border-[#0A1C40]"
                }`}
                data-testid={`pallet-cat-${c.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {c === "all" ? "All categories" : c}
              </button>
            ))}
          </div>
        )}
        {filtered.length === 0 && (
          <div className="border-2 border-dashed border-gray-300 p-10 text-center max-w-4xl" data-testid="pallet-picker-empty">
            <div className="font-display font-bold text-lg">No selection items match</div>
            <p className="text-sm text-gray-600 mt-1">
              Try another category or search — or call/text <a href="tel:6164100158" className="font-bold underline">(616) 410-0158</a> and we'll build your pallet with you.
            </p>
          </div>
        )}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 pb-32">
          {filtered.map((p) => {
            const count = picks[p.id] || 0;
            return (
              <div
                key={p.id}
                className={`border-2 p-3 flex flex-col ${count > 0 ? "border-[#FFC220] bg-amber-50/40" : "border-gray-200"}`}
                data-testid={`pallet-item-${p.id}`}
              >
                <div className="aspect-square bg-[#F4F4F5] overflow-hidden mb-2">
                  {(p.images || [])[0] && (
                    <img src={fileUrl(p.images[0])} alt={p.name} className="w-full h-full object-cover" loading="lazy" />
                  )}
                </div>
                <div className="text-xs font-bold leading-snug line-clamp-2">{p.name}</div>
                <div className="text-[10px] text-gray-500 mt-0.5">Included in pallet price · {p.stock} left</div>
                <div className="mt-auto pt-2">
                  {count === 0 ? (
                    <button
                      type="button"
                      onClick={() => adjust(p, 1)}
                      className="w-full border-2 border-black text-xs font-bold py-1.5 hover:bg-black hover:text-white transition-colors [touch-action:manipulation]"
                      data-testid={`pallet-add-${p.id}`}
                    >
                      + Add to pallet
                    </button>
                  ) : (
                    <div className="flex items-center justify-between border-2 border-[#FFC220] bg-white">
                      <button type="button" onClick={() => adjust(p, -1)} className="p-1.5 [touch-action:manipulation]" data-testid={`pallet-dec-${p.id}`}>
                        <Minus className="w-3.5 h-3.5" />
                      </button>
                      <span className="text-sm font-black">{count}</span>
                      <button type="button" onClick={() => adjust(p, 1)} className="p-1.5 [touch-action:manipulation]" data-testid={`pallet-inc-${p.id}`}>
                        <Plus className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* STICKY PROGRESS BAR */}
      <div className="fixed bottom-0 left-0 right-0 bg-[#0A1C40] text-white z-40 [padding-bottom:env(safe-area-inset-bottom)]" data-testid="pallet-progress-bar">
        <div className="h-1 bg-white/10">
          <div className="h-full bg-[#FFC220] transition-[width]" style={{ width: `${Math.min(100, (selectedCount / (target || 1)) * 100)}%` }} />
        </div>
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 py-4 flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <span className="font-display font-black text-xl" data-testid="pallet-progress-count">{selectedCount}/{target}</span>
            <span className="text-white/60 text-sm ml-2">items picked</span>
            {tier && (
              <span className="ml-4 text-[#FFC220] font-bold" data-testid="pallet-progress-total">
                <span className="hidden sm:inline">{fmtMoney(tier.price)} pallet + {fmtMoney(fee)} fee = {fmtMoney(tier.price + fee)} — merchandise included</span>
                <span className="sm:hidden">{fmtMoney(tier.price + fee)} total</span>
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={addToCart}
            disabled={remaining > 0}
            className={`px-6 py-3 font-bold text-sm [touch-action:manipulation] ${
              remaining > 0 ? "bg-white/10 text-white/40 cursor-not-allowed" : "bg-[#FFC220] text-black hover:bg-[#e6ae1c]"
            }`}
            data-testid="pallet-add-to-cart-btn"
          >
            {remaining > 0 ? `Pick ${remaining} more item${remaining > 1 ? "s" : ""} to finish your pallet` : "Add pallet to cart →"}
          </button>
        </div>
      </div>

      {/* 21+ confirmation for lighters / tobacco accessories in the builder */}
      {agePrompt && (
        <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-6" data-testid="age-gate-modal">
          <div className="bg-white max-w-md w-full p-8 border-4 border-[#0A1C40]">
            <div className="ipv-eyebrow text-[#DC2626]">21 and over · ID checked on every purchase</div>
            <h3 className="font-display font-black text-2xl mt-2">Age-restricted item</h3>
            <p className="text-sm text-gray-700 mt-3">
              <b>{agePrompt.name}</b> is sold to adults 21+ only. Your ID will be checked at pickup or delivery.
              Confirm you are 21 or older to add it to your pallet.
            </p>
            <div className="mt-6 flex gap-3">
              <button
                type="button"
                className="btn-primary flex-1"
                onClick={() => {
                  setAgeOk(true);
                  const p = agePrompt;
                  setAgePrompt(null);
                  setPicks((prev) => {
                    if (selectedCount >= target) {
                      toast.error(`Your ${target}-item pallet is full — remove something first or pick a bigger pallet`);
                      return prev;
                    }
                    return { ...prev, [p.id]: (prev[p.id] || 0) + 1 };
                  });
                }}
                data-testid="age-gate-confirm-btn"
              >
                I'm 21 or older
              </button>
              <button type="button" className="btn-outline flex-1" onClick={() => setAgePrompt(null)} data-testid="age-gate-cancel-btn">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
